import React, { useCallback, useState, useRef, useEffect } from 'react';
import { css } from '@compiled/react';
import Textfield from '@atlaskit/textfield';
import SearchIcon from '@atlaskit/icon/glyph/search';

const MIN_SEARCH_CHARS = 3;

const COUNTRY_OPTIONS = [
  'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Antigua and Barbuda',
  'Argentina', 'Armenia', 'Australia', 'Austria', 'Azerbaijan', 'Bahamas', 'Bahrain',
  'Bangladesh', 'Barbados', 'Belarus', 'Belgium', 'Belize', 'Benin', 'Bhutan',
  'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei', 'Bulgaria',
  'Burkina Faso', 'Burundi', 'Cabo Verde', 'Cambodia', 'Cameroon', 'Canada',
  'Central African Republic', 'Chad', 'Chile', 'China', 'Colombia', 'Comoros',
  'Congo (Brazzaville)', 'Congo (Kinshasa)', 'Costa Rica', 'Croatia', 'Cuba',
  'Cyprus', 'Czech Republic', 'Denmark', 'Djibouti', 'Dominica', 'Dominican Republic',
  'Ecuador', 'Egypt', 'El Salvador', 'Equatorial Guinea', 'Eritrea', 'Estonia',
  'Eswatini', 'Ethiopia', 'Fiji', 'Finland', 'France', 'Gabon', 'Gambia', 'Georgia',
  'Germany', 'Ghana', 'Greece', 'Grenada', 'Guatemala', 'Guinea', 'Guinea-Bissau',
  'Guyana', 'Haiti', 'Honduras', 'Hungary', 'Iceland', 'India', 'Indonesia', 'Iran',
  'Iraq', 'Ireland', 'Israel', 'Italy', 'Jamaica', 'Japan', 'Jordan', 'Kazakhstan',
  'Kenya', 'Kiribati', 'Kosovo', 'Kuwait', 'Kyrgyzstan', 'Laos', 'Latvia', 'Lebanon',
  'Lesotho', 'Liberia', 'Libya', 'Liechtenstein', 'Lithuania', 'Luxembourg',
  'Madagascar', 'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta', 'Marshall Islands',
  'Mauritania', 'Mauritius', 'Mexico', 'Micronesia', 'Moldova', 'Monaco', 'Mongolia',
  'Montenegro', 'Morocco', 'Mozambique', 'Myanmar', 'Namibia', 'Nauru', 'Nepal',
  'Netherlands', 'New Zealand', 'Nicaragua', 'Niger', 'Nigeria', 'North Korea',
  'North Macedonia', 'Norway', 'Oman', 'Pakistan', 'Palau', 'Palestine', 'Panama',
  'Papua New Guinea', 'Paraguay', 'Peru', 'Philippines', 'Poland', 'Portugal', 'Qatar',
  'Romania', 'Russia', 'Rwanda', 'Saint Kitts and Nevis', 'Saint Lucia',
  'Saint Vincent and the Grenadines', 'Samoa', 'San Marino', 'Sao Tome and Principe',
  'Saudi Arabia', 'Senegal', 'Serbia', 'Seychelles', 'Sierra Leone', 'Singapore',
  'Slovakia', 'Slovenia', 'Solomon Islands', 'Somalia', 'South Africa', 'South Korea',
  'South Sudan', 'Spain', 'Sri Lanka', 'Sudan', 'Suriname', 'Sweden', 'Switzerland',
  'Syria', 'Taiwan', 'Tajikistan', 'Tanzania', 'Thailand', 'Timor-Leste', 'Togo',
  'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan', 'Tuvalu',
  'Uganda', 'Ukraine', 'United Arab Emirates', 'United Kingdom', 'United States',
  'Uruguay', 'Uzbekistan', 'Vanuatu', 'Vatican City', 'Venezuela', 'Vietnam',
  'Yemen', 'Zambia', 'Zimbabwe',
];

type Props = {
  value: string;
  onChange: (value: string) => void;
  isInvalid?: boolean;
  inputId: string;
};

/**
 * Salesforce-style Country field: search input with magnifying glass on left,
 * "Search..." placeholder, no dropdown arrow. Shows filtered options when typing 3+ chars.
 */
export const CountrySearchField: React.FC<Props> = ({
  value,
  onChange,
  isInvalid,
  inputId,
}) => {
  const [searchInput, setSearchInput] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const displayValue = isFocused ? searchInput : value;
  const showDropdown = isOpen && searchInput.length >= MIN_SEARCH_CHARS;

  const filteredOptions = useCallback(() => {
    if (searchInput.length < MIN_SEARCH_CHARS) return [];
    const lower = searchInput.toLowerCase();
    return COUNTRY_OPTIONS.filter((c) => c.toLowerCase().includes(lower)).slice(0, 10);
  }, [searchInput]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = (e.target as HTMLInputElement).value;
    setSearchInput(v);
    setIsOpen(true);
  };

  const handleSelect = (country: string) => {
    onChange(country);
    setSearchInput('');
    setIsOpen(false);
  };

  const handleFocus = () => {
    setIsFocused(true);
    setSearchInput('');
    setIsOpen(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
    setTimeout(() => setIsOpen(false), 200);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const options = filteredOptions();

  return (
    <div ref={containerRef} css={containerStyles} className="champion-form-country-search">
      <Textfield
        id={inputId}
        value={displayValue}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        isCompact
        placeholder="Search..."
        isInvalid={isInvalid}
        elemBeforeInput={
          <span css={searchIconStyles}>
            <SearchIcon label="" size="small" />
          </span>
        }
      />
      {showDropdown && options.length > 0 && (
        <div css={dropdownStyles}>
          {options.map((country) => (
            <div
              key={country}
              css={optionStyles}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(country);
              }}
            >
              {country}
            </div>
          ))}
        </div>
      )}
      {showDropdown && searchInput.length >= MIN_SEARCH_CHARS && options.length === 0 && (
        <div css={dropdownStyles}>
          <div css={noOptionsStyles}>No countries found</div>
        </div>
      )}
    </div>
  );
};

const containerStyles = css({
  position: 'relative',
  width: '100%',
});

const searchIconStyles = css({
  display: 'flex',
  alignItems: 'center',
  color: '#706e6b',
  paddingLeft: '0.5rem',
});

const dropdownStyles = css({
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  marginTop: '2px',
  backgroundColor: '#fff',
  border: '1px solid #c9c9c9',
  borderRadius: '0.25rem',
  boxShadow: '0 2px 3px 0 rgba(0, 0, 0, 0.16)',
  maxHeight: '150px',
  overflowY: 'auto',
  zIndex: 100,
});

const optionStyles = css({
  padding: '0.5rem 0.75rem',
  fontSize: '0.8125rem',
  color: '#080707',
  cursor: 'pointer',
  ':hover': {
    backgroundColor: '#f3f2f2',
  },
});

const noOptionsStyles = css({
  padding: '0.5rem 0.75rem',
  fontSize: '0.8125rem',
  color: '#706e6b',
});
